import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import bonusService, {
  formatSubjectLabel,
} from "../../services/bonusService";
import { getInitials } from "../../utils/stringUtils";

function parseBonusDebugJson(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

const STATUS_CONFIG = {
  COMPLETE: {
    label: "Complete",
    pill: "bg-green-50 text-green-700 border-green-200",
    dot: "bg-green-500",
    icon: "fas fa-check-circle",
  },
  OVERRIDDEN: {
    label: "Overridden",
    pill: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-400",
    icon: "fas fa-pen",
  },
  MISSING_DATA: {
    label: "Open data",
    pill: "bg-gray-50 text-gray-500 border-gray-200",
    dot: "bg-gray-300",
    icon: "fas fa-clock",
  },
  NOT_CALCULATED: {
    label: "Not calculated",
    pill: "bg-gray-50 text-gray-500 border-gray-200",
    dot: "bg-gray-300",
    icon: "fas fa-minus-circle",
  },
};

function StatusBadge({ status }) {
  const sc = STATUS_CONFIG[status] || STATUS_CONFIG.NOT_CALCULATED;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${sc.pill}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
      {sc.label}
    </span>
  );
}

function OverrideTag({ calculated, override }) {
  if (override == null) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      <span className="text-[10px] text-amber-600 font-semibold">Override</span>
      <span className="text-[10px] text-grey-dusk font-semibold">
        {bonusService.formatPercent(override)}
      </span>
      {calculated != null && (
        <span className="text-[10px] text-gray-400 line-through">
          {bonusService.formatPercent(calculated)}
        </span>
      )}
    </div>
  );
}

function reviewOptionLabel(review) {
  const title = review.title || `Review #${review.reviewId}`;
  const by = review.creatorName ? ` · ${review.creatorName}` : "";
  return `${title}${by}`;
}

/**
 * Lets an Admin/Berufsbildner pick which report or assessment feeds this
 * source. Auto-selection stays the default; picking an entry pins it, which
 * only matters when more than one candidate exists but is always shown so the
 * chosen source is visible rather than implicit.
 */
function ReviewSelector({ label, availableReviews, currentReviewId, pinReviewId, onSelect, saving }) {
  const reviews = availableReviews || [];
  if (reviews.length === 0) return null;

  const activeId = pinReviewId ?? currentReviewId;
  const autoSelected = reviews.find((r) => r.reviewId === currentReviewId);

  return (
    <div className="mt-2.5 pt-2 border-t border-pale-dawn/70">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[10px] font-semibold text-gray-500">{label}</span>
        {pinReviewId != null && (
          <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-700 text-[9px] font-semibold">
            Pinned
          </span>
        )}
      </div>

      <select
        aria-label={label}
        value={pinReviewId ?? ""}
        disabled={saving}
        onChange={(e) =>
          onSelect(e.target.value === "" ? null : Number(e.target.value))
        }
        className={`w-full text-[11px] px-2 py-1.5 rounded-lg border bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 disabled:opacity-50 ${
          pinReviewId != null ? "border-amber-300 bg-amber-50/40" : "border-pale-dawn"
        }`}
      >
        <option value="">
          {autoSelected
            ? `Automatic · ${reviewOptionLabel(autoSelected)}`
            : "Automatic"}
        </option>
        {reviews.map((r) => (
          <option key={r.reviewId} value={r.reviewId}>
            {reviewOptionLabel(r)}
            {r.status ? ` (${r.status})` : ""}
          </option>
        ))}
      </select>

      {reviews.length > 1 && pinReviewId == null && (
        <p className="mt-1 text-[10px] text-gray-400">
          {reviews.length} available - automatic picked
          {activeId != null ? ` #${activeId}` : ""}.
        </p>
      )}
    </div>
  );
}

function SourceCell({
  label,
  calculated,
  override,
  final: finalVal,
  missing,
  missingLabel,
  editing,
  editValue,
  onEdit,
  meta = [],
  creatorName,
  availableReviews,
  currentReviewId,
  pinReviewId,
  onPinReview,
  pinSaving,
}) {
  const isOverridden = override != null;
  const isComplete = !missing;
  const isEditing = editing;

  return (
    <div
      className={`rounded-xl border px-3 py-3 transition-colors
        ${isEditing ? "border-amber-300 bg-amber-50/40" : isOverridden ? "border-amber-200 bg-amber-50/70" : isComplete ? "border-green-100 bg-green-50/40" : "border-pale-dawn bg-ice-white/60"}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-grey-dusk truncate">{label}</p>
          {!isEditing && (isComplete ? (
            <span className="inline-flex items-center mt-1 text-[10px] font-semibold text-green-700">
              <i className="fas fa-check-circle mr-1"></i>Complete
            </span>
          ) : (
            <span className="inline-flex items-center mt-1 text-[10px] font-medium text-gray-500">
              <i className="fas fa-circle-info mr-1"></i>Not available yet
            </span>
          ))}
        </div>
        {!isEditing && (isOverridden || !missing) && (
          <div className={`text-base font-bold shrink-0 ${isOverridden ? "text-amber-700" : "text-grey-dusk"}`}>
            {bonusService.formatPercent(isOverridden ? override : finalVal)}
          </div>
        )}
      </div>

      {!isEditing && (
        missing ? (
          <>
            {isOverridden && (
              <div className="flex flex-wrap items-center gap-1 mb-1">
                <span className="text-[10px] text-amber-600 font-semibold">Override</span>
                <span className="text-[10px] text-grey-dusk font-semibold">{bonusService.formatPercent(override)}</span>
              </div>
            )}
            <p className="text-[11px] leading-snug text-gray-500">
              {missingLabel || "Source data is not ready yet."}
            </p>
            {/* Nothing matched automatically, but a candidate may still exist
                that staff can point this source at. */}
            <ReviewSelector
              label={`Used ${label}`}
              availableReviews={availableReviews}
              currentReviewId={currentReviewId}
              pinReviewId={pinReviewId}
              onSelect={onPinReview}
              saving={pinSaving}
            />
          </>
        ) : (
          <>
            <OverrideTag calculated={calculated} override={override} />
            {(meta.length > 0 || creatorName) && (
              <div className="mt-2 space-y-0.5 text-[10px] text-gray-500">
                {creatorName && <p><i className="fas fa-user mr-1 text-gray-400" />Von: {creatorName}</p>}
                {meta.map((line, idx) => <p key={`${label}-meta-${idx}`}>{line}</p>)}
              </div>
            )}
            <ReviewSelector
              label={`Used ${label}`}
              availableReviews={availableReviews}
              currentReviewId={currentReviewId}
              pinReviewId={pinReviewId}
              onSelect={onPinReview}
              saving={pinSaving}
            />
          </>
        )
      )}

      {isEditing && (
        <div className="mt-1">
          {!missing && finalVal != null && (
            <p className="text-[11px] text-gray-500 mb-1.5">
              Current: <span className={`font-semibold ${isOverridden ? "text-amber-600" : "text-grey-dusk"}`}>{bonusService.formatPercent(isOverridden ? override : finalVal)}</span>
              {isOverridden && <span className="ml-1 text-gray-400 line-through">{bonusService.formatPercent(calculated)}</span>}
            </p>
          )}
          <div className="relative">
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder={isOverridden ? String(override) : calculated != null ? String(calculated) : "—"}
              value={editValue || ""}
              onChange={(e) => onEdit(e.target.value)}
              className="w-full px-3 py-1.5 pr-7 text-xs border border-amber-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white"
            />
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 pointer-events-none">%</span>
          </div>
        </div>
      )}
    </div>
  );
}

function SemesterCard({ sem, studentId, onUpdated, open, onToggle }) {
  const [showGrades, setShowGrades] = useState(false);
  const [pinSaving, setPinSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState({});
  const [semesterPercentEdit, setSemesterPercentEdit] = useState("");
  const [finalBonusEdit, setFinalBonusEdit] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const semesterCode = sem.semesterCode || `S${sem.semesterNumber}`;

  function setEdit(field, val) {
    setEdits((prev) => ({ ...prev, [field]: val }));
  }

  function openEdit() {
    setEdits({
      educationReportOverride: sem.educationReportOverride != null ? String(sem.educationReportOverride) : "",
      semesterReviewOverride: sem.semesterReviewOverride != null ? String(sem.semesterReviewOverride) : "",
      schoolGradeOverride: sem.schoolGradeOverride != null ? String(sem.schoolGradeOverride) : "",
    });
    setSemesterPercentEdit("");
    setFinalBonusEdit("");
    setSaveError(null);
    setEditing(true);
    // Editing a collapsed semester would hide the inputs.
    if (!open) onToggle();
  }

  function cancelEdit() {
    setEditing(false);
    setEdits({});
    setSemesterPercentEdit("");
    setFinalBonusEdit("");
    setSaveError(null);
  }

  async function saveEdits() {
    setSaving(true);
    setSaveError(null);
    const parse = (v) => (v !== "" && v != null ? parseFloat(v) : null);
    try {
      const updated = await bonusService.saveOverrides(studentId, {
        semesterOverrides: [{
          semesterNumber: sem.semesterNumber,
          educationReportOverride: parse(edits.educationReportOverride),
          semesterReviewOverride: parse(edits.semesterReviewOverride),
          schoolGradeOverride: parse(edits.schoolGradeOverride),
          semesterPercentOverride: parse(semesterPercentEdit),
          finalBonusOverride: parse(finalBonusEdit),
        }],
      });
      setEditing(false);
      setEdits({});
      setFinalBonusEdit("");
      onUpdated(updated);
    } catch {
      setSaveError("Failed to save overrides.");
    } finally {
      setSaving(false);
    }
  }

  const debugData = parseBonusDebugJson(sem.debugSourceJson);
  const schoolGradesDebug = debugData?.schoolGrades || null;
  const schoolGradesMissingReason = sem.schoolGradesMissing
    ? schoolGradesDebug?.missingReason ||
      "School grades are not checked/finalized for this semester yet."
    : null;

  async function handlePinReview(sourceType, reviewId) {
    setPinSaving(true);
    try {
      const updated = await bonusService.pinReview(studentId, {
        semesterNumber: sem.semesterNumber,
        sourceType,
        reviewId,
      });
      onUpdated(updated);
    } catch {
      // silently ignore — backend error will not crash UI
    } finally {
      setPinSaving(false);
    }
  }

  const missingCount = [
    sem.educationReportMissing,
    sem.semesterReviewMissing,
    sem.schoolGradesMissing,
  ].filter(Boolean).length;
  const completeCount = 3 - missingCount;
  const semesterReady = missingCount === 0;
  const displayStatus = sem.status === "OVERRIDDEN" ? "COMPLETE" : sem.status;

  return (
    <div className={`card-brand overflow-hidden bg-white transition-all ${editing ? "ring-2 ring-amber-300" : ""}`}>
      {/* Semester header */}
      <div className={`px-5 py-4 border-b border-pale-dawn ${editing ? "bg-amber-50/60" : "bg-gradient-to-r from-ice-white to-pale-dawn/30"}`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="text-left min-w-0 group"
          >
            <div className="flex flex-wrap items-center gap-2">
              <i
                className={`fas fa-chevron-right text-sunrise-red text-sm transition-transform ${open ? "rotate-90" : ""}`}
              />
              <h3 className="text-lg font-bold text-grey-dusk group-hover:text-sunrise-red transition-colors">
                {sem.semesterLabel || `Semester ${sem.semesterNumber}`}
              </h3>
              <StatusBadge status={displayStatus} />
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              {semesterReady ? "All sources complete." : `${completeCount}/3 sources complete`}
            </p>
          </button>
          {!editing ? (
            <button
              onClick={openEdit}
              className="self-start lg:self-auto text-xs text-gray-500 hover:text-sunrise-red transition-colors flex items-center gap-1.5 shrink-0"
            >
              <i className="fas fa-pen text-[10px]"></i>
              Override values
            </button>
          ) : (
            <div className="flex items-center gap-2 self-start lg:self-auto">
              <button
                onClick={saveEdits}
                disabled={saving}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sunrise-red text-white hover:bg-sunrise-600 disabled:opacity-50 flex items-center gap-1"
              >
                {saving ? <><i className="fas fa-spinner fa-spin text-[9px]" /> Saving…</> : "Save"}
              </button>
              <button
                onClick={cancelEdit}
                className="px-3 py-1.5 rounded-lg text-xs border border-pale-dawn text-gray-600 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bonus summary row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-pale-dawn border-b border-pale-dawn">
        <div className="bg-white px-4 py-4 text-center">
          <p className="text-xs text-gray-500">Semester %</p>
          <p className="text-xl font-bold text-grey-dusk">
            {bonusService.formatPercent(sem.semesterPercentFinal)}
          </p>
        </div>
        <div className="bg-white px-4 py-4 text-center">
          <p className="text-xs text-gray-500">Configured Amount = 100%</p>
          <p className="text-xl font-bold text-grey-dusk">
            {bonusService.formatCHF(sem.configuredAmount)}
          </p>
        </div>
        <div
          className={`${semesterReady || sem.status === "OVERRIDDEN" ? "bg-green-50" : "bg-white"} px-4 py-4 text-center`}
        >
          <p className="text-xs text-gray-500">Final Bonus</p>
          <p
            className={`text-xl font-bold ${sem.finalBonus > 0 ? "text-sunrise-red" : "text-grey-dusk"}`}
          >
            {bonusService.formatCHF(sem.finalBonus)}
          </p>
        </div>
      </div>

      {/* Formula */}
      {open && sem.calculationFormula && semesterReady && (
        <div className="px-5 py-2 bg-ice-white/60 border-b border-pale-dawn">
          <p className="text-[10px] font-mono text-gray-500">
            {sem.calculationFormula}
          </p>
        </div>
      )}

      {/* Source cells */}
      {open && (
      <div className="p-4">
        <div className={`rounded-2xl border p-4 ${editing ? "border-amber-200 bg-white" : semesterReady ? "border-green-100 bg-white" : "border-pale-dawn bg-white"}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <SourceCell
              label="Bildungsbericht"
              calculated={sem.educationReportCalculated}
              override={sem.educationReportOverride}
              final={sem.educationReportFinal}
              missing={sem.educationReportMissing}
              missingLabel={`No completed report matched ${semesterCode}.`}
              editing={editing}
              editValue={edits.educationReportOverride}
              onEdit={(v) => setEdit("educationReportOverride", v)}
              creatorName={sem.educationReportCreatorName}
              availableReviews={sem.availableEducationReports}
              currentReviewId={sem.educationReportReviewId}
              pinReviewId={sem.educationReportReviewPinId}
              onPinReview={(reviewId) => handlePinReview("EDUCATION_REPORT", reviewId)}
              pinSaving={pinSaving}
              meta={
                sem.educationReportPoints != null || sem.educationReportMaxPoints != null
                  ? [
                      `Punkte: ${sem.educationReportPoints ?? "—"} / ${sem.educationReportMaxPoints ?? "—"}`,
                      `Berechnet: ${bonusService.formatPercent(sem.educationReportCalculated)}`,
                    ]
                  : [`Berechnet: ${bonusService.formatPercent(sem.educationReportCalculated)}`]
              }
            />

            <SourceCell
              label="Semestergespräch"
              calculated={sem.semesterReviewAveragePercent ?? sem.semesterReviewCalculated}
              override={sem.semesterReviewOverride}
              final={sem.semesterReviewFinal}
              missing={sem.semesterReviewMissing}
              missingLabel={`No completed assessment matched ${semesterCode}.`}
              editing={editing}
              editValue={edits.semesterReviewOverride}
              onEdit={(v) => setEdit("semesterReviewOverride", v)}
              creatorName={sem.semesterReviewCreatorName}
              availableReviews={sem.availableSemesterReviews}
              currentReviewId={sem.semesterReviewId}
              pinReviewId={sem.semesterReviewPinId}
              onPinReview={(reviewId) => handlePinReview("SEMESTER_REVIEW", reviewId)}
              pinSaving={pinSaving}
              meta={
                sem.semesterReviewMissing
                  ? []
                  : [
                      `Pflichterfüllung: ${bonusService.formatPercent(sem.semesterReviewPflichtPercent)}`,
                      `Motivation: ${bonusService.formatPercent(sem.semesterReviewMotivationPercent)}`,
                    ]
              }
            />

            <SourceCell
              label="Schulnoten"
              calculated={sem.schoolGradeCalculated}
              override={sem.schoolGradeOverride}
              final={sem.schoolGradeFinal}
              missing={sem.schoolGradesMissing}
              missingLabel={schoolGradesMissingReason || `No checked grades for semester ${sem.semesterNumber}.`}
              editing={editing}
              editValue={edits.schoolGradeOverride}
              onEdit={(v) => setEdit("schoolGradeOverride", v)}
              meta={
                sem.schoolGradesMissing
                  ? []
                  : [
                      `Verwendete Noten: ${sem.grades?.length ?? 0}`,
                      `Durchschnitt: ${sem.schoolGradeAverage != null ? Number(sem.schoolGradeAverage).toFixed(1) : "—"}`,
                      `Gemappt: ${bonusService.formatPercent(sem.schoolGradeCalculated)}`,
                    ]
              }
            />
          </div>

          {!editing && sem.calculationFormula && sem.semesterPercentFinal != null && (
            <div className="rounded-xl bg-ice-white border border-pale-dawn px-3 py-2 text-xs text-gray-500 font-mono mb-2">
              {sem.calculationFormula}
            </div>
          )}

          {/* Semester % and Final Bonus override rows (editing only) */}
          {editing && (
            <div className="mt-3 pt-3 border-t border-amber-200 space-y-2.5">
              {/* Semester % override */}
              <div className="flex items-center gap-3">
                <div className="w-32 shrink-0">
                  <p className="text-xs font-semibold text-grey-dusk">Semester %</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {sem.semesterPercentFinal != null
                      ? <span className="font-semibold text-grey-dusk">{bonusService.formatPercent(sem.semesterPercentFinal)}</span>
                      : <span className="text-gray-400">Not calculated</span>}
                  </p>
                </div>
                <div className="relative flex-1">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="200"
                    placeholder={sem.semesterPercentFinal != null ? String(sem.semesterPercentFinal) : "e.g. 100"}
                    value={semesterPercentEdit}
                    onChange={(e) => setSemesterPercentEdit(e.target.value)}
                    className={`w-full px-3 py-1.5 pr-7 text-xs border rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white ${semesterPercentEdit ? "border-amber-400 bg-amber-50/30" : "border-pale-dawn"}`}
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 pointer-events-none">%</span>
                </div>
              </div>

              {/* Final Bonus override */}
              <div className="flex items-center gap-3">
                <div className="w-32 shrink-0">
                  <p className="text-xs font-semibold text-grey-dusk">Final Bonus</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {sem.finalBonus != null
                      ? <span className="font-semibold text-grey-dusk">{bonusService.formatCHF(sem.finalBonus)}</span>
                      : <span className="text-gray-400">Not calculated</span>}
                  </p>
                </div>
                <div className="relative flex-1">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 pointer-events-none">CHF</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={sem.finalBonus != null ? String(sem.finalBonus) : "0.00"}
                    value={finalBonusEdit}
                    onChange={(e) => setFinalBonusEdit(e.target.value)}
                    className={`w-full pl-10 pr-3 py-1.5 text-xs border rounded-lg focus:outline-none focus:ring-1 focus:ring-amber-400 bg-white ${finalBonusEdit ? "border-amber-400 bg-amber-50/30" : "border-pale-dawn"}`}
                  />
                </div>
              </div>

              <p className="text-[10px] text-gray-400">
                Setting Semester % computes the bonus automatically. Final Bonus overrides it directly.
              </p>
            </div>
          )}

          {!editing && !sem.schoolGradesMissing && sem.grades?.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => setShowGrades(!showGrades)}
                className="text-xs text-gray-500 hover:text-sunrise-red transition-colors flex items-center gap-1"
              >
                <i className={`fas fa-chevron-${showGrades ? "up" : "down"} text-[10px]`} />
                {showGrades ? "Hide" : "Show"} {sem.grades.length} grade{sem.grades.length !== 1 ? "s" : ""}
                {sem.schoolGradeAverage != null && <span className="ml-1">· avg {Number(sem.schoolGradeAverage).toFixed(1)}</span>}
              </button>

              {showGrades && (
                <div className="mt-2 rounded-xl border border-pale-dawn overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-ice-white border-b border-pale-dawn">
                        <th className="text-left px-3 py-2 text-gray-500 font-semibold">Subject</th>
                        <th className="text-left px-3 py-2 text-gray-500 font-semibold">School</th>
                        <th className="text-left px-3 py-2 text-gray-500 font-semibold">Class</th>
                        <th className="text-right px-3 py-2 text-gray-500 font-semibold">Grade</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-pale-dawn">
                      {sem.grades.map((g, idx) => (
                        <tr key={idx} className="hover:bg-ice-white/70">
                          <td className="px-3 py-2 text-gray-600">{formatSubjectLabel(g)}</td>
                          <td className="px-3 py-2 text-gray-500">{g.schoolName || "—"}</td>
                          <td className="px-3 py-2 text-gray-500">{g.className || "—"}</td>
                          <td className="px-3 py-2 text-right font-mono text-grey-dusk">{Number(g.definitiveGrade).toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                    {sem.schoolGradeAverage != null && (
                      <tfoot>
                        <tr className="bg-ice-white border-t border-pale-dawn">
                          <td className="px-3 py-2 font-semibold text-grey-dusk">Durchschnitt</td>
                          <td colSpan={2} />
                          <td className="px-3 py-2 text-right font-mono font-bold text-grey-dusk">{Number(sem.schoolGradeAverage).toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          )}

          {!editing && !sem.schoolGradesFinalized && !sem.schoolGradesMissing && (
            <p className="mt-3 text-xs text-gray-500 flex items-center">
              <i className="fas fa-circle-info mr-2 text-sunrise-red"></i>
              Grades are available, but not finalized by Revisor yet.
            </p>
          )}

          {saveError && editing && (
            <p className="mt-2 text-xs text-red-600">{saveError}</p>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

export default function BonusStudentDetailPage() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [error, setError] = useState(null);
  // One semester expanded at a time; defaults to the learner's current one.
  const [openSemester, setOpenSemester] = useState(null);

  useEffect(() => {
    const list = detail?.semesters || [];
    if (list.length === 0) return;
    // Prefer the semester the learner is in; if the backend reports one that
    // has no calculation row yet, fall back to the latest so the card is never
    // fully collapsed on arrival.
    const current =
      list.find((s) => s.semesterNumber === detail?.currentSemesterNumber) ||
      list[list.length - 1];
    setOpenSemester((prev) => prev ?? current.semesterNumber);
  }, [detail]);

  async function handleRecalculate() {
    setRecalculating(true);
    setError(null);
    try {
      const updated = await bonusService.recalculate(studentId);
      setDetail(updated);
    } catch (e) {
      setError("Recalculation failed.");
    } finally {
      setRecalculating(false);
    }
  }

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    bonusService
      .recalculate(studentId)
      .then((data) => {
        if (active) {
          setDetail(data);
          setError(null);
        }
      })
      .catch(() => {
        if (!active) return;
        bonusService
          .getStudentDetail(studentId)
          .then((data) => {
            if (active) setDetail(data);
          })
          .catch(() => {
            if (active) setError("Failed to load student bonus data.");
          });
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [studentId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-green-cloud">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
          <div className="h-6 w-48 bg-white/70 rounded animate-pulse" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-24 bg-white rounded-xl animate-pulse" />
            ))}
          </div>
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-56 bg-white rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-green-cloud">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!detail) return null;

  const semesters = detail.semesters || [];
  const totalBonus =
    semesters
      .filter((s) => s.finalBonus != null)
      .reduce((sum, s) => sum + parseFloat(s.finalBonus), 0) || 0;

  const completedSemesters = semesters.filter(
    (s) => s.status === "COMPLETE" || s.status === "OVERRIDDEN",
  ).length;

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 flex items-center text-sunrise-red hover:text-sunrise-600 transition-colors"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Back to Bonus Overview
        </button>

        <div className="card-brand-elevated p-6 bg-gradient-to-r from-ice-white to-pale-dawn/30 mb-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-sunrise-red text-white flex items-center justify-center text-lg font-bold shadow-sm">
                {getInitials(detail.firstName, detail.lastName, detail.fullName)}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-grey-dusk">
                  {detail.fullName}
                </h1>
              </div>
            </div>

            <button
              onClick={handleRecalculate}
              disabled={recalculating}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-sunrise-red text-white hover:bg-sunrise-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              <i className={`fas fa-sync-alt text-xs ${recalculating ? "animate-spin" : ""}`}></i>
              {recalculating ? "Calculating..." : "Recalculate"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <SummaryCard
            label="Overall Status"
            value={
              <StatusBadge
                status={
                  detail.overallStatus === "OVERRIDDEN" ? "COMPLETE" : detail.overallStatus
                }
              />
            }
          />
          <SummaryCard
            label="Total Bonus"
            value={
              <span className={`text-xl font-bold ${totalBonus > 0 ? "text-sunrise-red" : "text-grey-dusk"}`}>
                {bonusService.formatCHF(totalBonus)}
              </span>
            }
          />
          <SummaryCard
            label="Complete Semesters"
            value={
              <span className="text-xl font-bold text-grey-dusk">
                {completedSemesters}/{semesters.length}
              </span>
            }
          />
        </div>

        {semesters.length === 0 ? (
          <div className="card-brand px-5 py-10 text-center text-sm text-gray-500">
            <i className="fas fa-calculator text-2xl text-gray-300 mb-3 block"></i>
            No calculation data yet. Click Recalculate to compute this student's bonus.
          </div>
        ) : (
          <div className="space-y-5">
            {semesters.map((sem) => (
              <SemesterCard
                key={sem.semesterNumber}
                sem={sem}
                studentId={studentId}
                open={openSemester === sem.semesterNumber}
                onToggle={() =>
                  setOpenSemester((prev) =>
                    prev === sem.semesterNumber ? null : sem.semesterNumber,
                  )
                }
                onUpdated={(updated) => updated ? setDetail(updated) : bonusService.getStudentDetail(studentId).then(setDetail)}
              />
            ))}
          </div>
        )}

        {semesters.length > 0 && (
          <div className="mt-5 card-brand px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-grey-dusk">Total Payout</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Sum of all completed semester payouts
                {detail.hasAnyOverride && (
                  <span className="ml-1 text-amber-600">· includes overrides</span>
                )}
              </p>
            </div>
            <p className="text-2xl font-bold text-sunrise-red">
              {bonusService.formatCHF(totalBonus)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="card-brand bg-white px-4 py-3">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <div>{value}</div>
    </div>
  );
}
